<template>
  <table v-if="items?.length && fields?.length" class="table-custom-large">
    <thead>
      <TableCellHeading>
        <slot name="checkbox-heading" />
      </TableCellHeading>
      <TableCellHeading
        v-for="field in fields"
        :key="field.key"
        :class="{ sort: field.sortable }"
        :tabindex="field.sortable ? 0 : -1"
        @click="handleSorting(field.key)"
        @keydown.enter="handleSorting(field.key)"
        @keydown.space="handleSorting(field.key)"
      >
        {{ field.name }}
        <SvgDisplay
          v-if="field.sortable && field.key === selectedSortKey && sortByDirection === 'asc'"
          src="Icon-Chevron-Down-Small"
        ></SvgDisplay>
        <SvgDisplay
          v-if="field.sortable && field.key === selectedSortKey && sortByDirection === 'desc'"
          src="Icon-Chevron-Up-Small"
        ></SvgDisplay>
        <SvgDisplay
          v-if="field.sortable && field.key !== selectedSortKey"
          src="Icon-chevrons-up-down"
        ></SvgDisplay>
      </TableCellHeading>
    </thead>
    <tbody>
      <TableRow
        v-for="item in items"
        :key="item.id"
        :class="{
          'has-sorting': hasSorting,
          selected: selectedIds.find((attachmentId) => attachmentId === item.id)
        }"
        @onClicked="(event) => handleRowClick(event, item)"
      >
        <TableCellData>
          <slot name="checkbox-data" :item="item" />
        </TableCellData>
        <TableCellData
          @mouseleave="showTooltip = false"
          @mouseenter="(e) => trackPointer(e, item.name)"
        >
          <p>{{ item.name }}</p>
          <TooltipStandard
            v-show="showTooltip"
            arrowPosition="top-center"
            class="tooltip"
            :pointerEvent="trackedMouseEvent"
            :text="hoveredTitle"
          />
        </TableCellData>
        <TableCellData
          @mouseleave="showTooltip = false"
          @mouseenter="(e) => trackPointer(e, item.actType ?? '')"
        >
          <p>{{ item.actType ?? '' }}</p>
          <TooltipStandard
            v-show="showTooltip"
            arrowPosition="top-center"
            class="tooltip"
            :pointerEvent="trackedMouseEvent"
            :text="hoveredTitle"
          />
        </TableCellData>
        <TableCellData
          @mouseleave="showTooltip = false"
          @mouseenter="(e) => trackPointer(e, item.actStatus ?? '')"
        >
          <p>{{ item.actStatus ?? '' }}</p>
          <TooltipStandard
            v-show="showTooltip"
            arrowPosition="top-center"
            class="tooltip"
            :pointerEvent="trackedMouseEvent"
            :text="hoveredTitle"
          />
        </TableCellData>
        <TableCellData
          @mouseleave="showTooltip = false"
          @mouseenter="(e) => trackPointer(e, item.name)"
        >
          <p>{{ item.createdDate ? new Date(item.createdDate).toLocaleDateString() : '' }}</p>
          <TooltipStandard
            v-show="showTooltip"
            arrowPosition="top-center"
            class="tooltip"
            :pointerEvent="trackedMouseEvent"
            :text="hoveredTitle"
          />
        </TableCellData>
        <TableCellData>
          <slot name="placement" :item="item" />
        </TableCellData>
      </TableRow>
    </tbody>
  </table>
  <table v-else class="table-custom-large">
    <thead>
      <TableCellHeading>
        {{ 0 + ' ' + itemType.toLowerCase() }}
      </TableCellHeading>
    </thead>
    <tbody>
      <TableRow>
        <TableCellData>
          {{
            t('general__text__no') +
            ' ' +
            itemType.toLowerCase() +
            ' ' +
            t('general__text__available').toLowerCase()
          }}
        </TableCellData>
      </TableRow>
    </tbody>
  </table>
</template>
<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import SvgDisplay from '../atoms/SvgDisplay.vue'
import TableCellData from '@/design-system/atoms/TableCellData.vue'
import TableCellHeading from '@/design-system/atoms/TableCellHeading.vue'
import TableRow from '@/design-system/atoms/TableRow.vue'
import TooltipStandard from '@/design-system/atoms/TooltipStandard.vue'
import { Direction } from '@/services/attachments.service'

export type TableItem = {
  id: string
  name: string
  entityId?: string
  fileType?: string
  createdDate?: string
  sorting?: string
  entityType: string
  actStatus?: string
  actType?: string
}

type Props = {
  fields: Array<any>
  hasArchiveButton?: boolean
  hasCreateButton?: boolean
  hasDeleteButton?: boolean
  hasDownloadButton?: boolean
  hasEditButton?: boolean
  hasMenu?: boolean
  hasSorting?: boolean
  items: TableItem[]
  itemType: string
  selectedIds: string[]
  resetSorting?: boolean
}
const props = defineProps<Props>()
type Emits = {
  (e: 'archiveItem'): void
  (e: 'deleteItem'): void
  (e: 'downloadItem'): void
  (e: 'editItem', item: TableItem): void
  (e: 'onClickedRow', item: TableItem, withinCheckbox: boolean): void
  (e: 'sortBy', key: string, value: Direction): void
}
const emits = defineEmits<Emits>()
const { t } = useI18n()
const hoveredTitle = ref<string>('')
const lastMousePosition = ref([0, 0])
const showTooltip = ref(false)
const sortByDirection = ref<Direction>('asc')
const selectedSortKey = ref<string>('name') // name or uploaded
const trackedMouseEvent = ref<object>()

const fields = computed(() => {
  return props.fields
})
function handleRowClick(event: Event, item: TableItem) {
  // @ts-expect-error - the target and previousSibling types would need to be cast to HTMLInputElement for typescript to not throw an error. This is not necessary for functionality.
  const withinCheckbox = event.target?.previousSibling?.type === 'checkbox'
  emits('onClickedRow', item, withinCheckbox)
}
function handleSorting(key: string) {
  sortByDirection.value = sortByDirection.value === 'asc' ? 'desc' : 'asc'
  if (key !== selectedSortKey.value) {
    sortByDirection.value = 'asc'
  }
  selectedSortKey.value = key
  emits('sortBy', key, sortByDirection.value)
}

function trackPointer(event, title: string) {
  hoveredTitle.value = title
  const rect = event.currentTarget.getBoundingClientRect()
  if (event) {
    if (
      lastMousePosition.value[0] === event.clientX &&
      lastMousePosition.value[1] === event.clientY
    )
      return
    lastMousePosition.value = [event.clientX, event.clientY]
    trackedMouseEvent.value = {
      x: rect.left + rect.width / 2,
      y: rect.bottom + rect.height / 2
    }
    const targetEl = event.target as HTMLElement
    if (targetEl.firstElementChild) {
      showTooltip.value =
        targetEl.firstElementChild.clientWidth < targetEl.firstElementChild.scrollWidth
    }
  }
}

watch(
  () => props.resetSorting,
  (newValue) => {
    if (newValue === true) {
      sortByDirection.value = 'asc'
    }
  }
)
</script>
<style lang="scss" scoped>
@use '@/design-system/design-tokens/font-mixins';
@use '@/design-system/design-tokens/variables.scss';
@use '@/design-system/design-tokens/mixins';
@use '@/design-system/design-tokens/font-variables.scss';

.button-no-padding {
  padding: 0;
}
.tooltip {
  filter: drop-shadow(0px 0px 0.6px rgb(0, 0, 0));
  margin-top: -2px;
  :deep(.tooltip__arrow) {
    filter: unset;
  }
}
.table-custom-large {
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  thead {
    align-items: center;
    background-color: rgba(variables.$color-primary-light, 0.4);
    display: flex;
    height: 48px;
    padding: 4px;
    width: 100%;
    .sort {
      cursor: pointer;
    }
    th {
      &:first-child {
        justify-content: center;
        min-width: 48px;
        width: 48px;
      }
      &:nth-child(2) {
        max-width: 30%;
        min-width: 30%;
        width: 30%;
      }
      &:nth-child(3) {
        max-width: 15%;
        min-width: 15%;
        width: 15%;
      }
      &:nth-child(4) {
        max-width: 15%;
        min-width: 15%;
        width: 15%;
      }
      &:nth-child(5) {
        min-width: 130px;
        width: 130px;
      }
      &:nth-child(6) {
        min-width: 80px;
        width: 80px;
      }
    }
  }
  tbody {
    display: flex;
    flex-flow: column;
    overflow-x: hidden;
    overflow-y: auto;
    width: 100%;
    @include mixins.scrollbar-vertical;
    tr {
      height: 48px;
      justify-content: flex-start;
      padding: 4px;
      width: 100%;
      p {
        color: variables.$color-greyscale-content;
        margin: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        @include font-mixins.desktop-regular-paragraph;
      }
      td {
        gap: 0;
        @include font-mixins.desktop-regular-paragraph;
        &:first-child {
          justify-content: center;
          min-width: 48px;
          width: 48px;
        }
        &:nth-child(2) {
          max-width: 30%;
          min-width: 30%;
          padding-right: 8px;
          width: 30%;
        }
        &:nth-child(3) {
          max-width: 15%;
          min-width: 15%;
          padding-right: 8px;
          width: 15%;
        }
        &:nth-child(4) {
          max-width: 15%;
          min-width: 15%;
          padding-right: 8px;
          width: 15%;
        }
        &:nth-child(5) {
          min-width: 130px;
          width: 130px;
        }
        &:nth-child(6) {
          min-width: 80px;
          width: 80px;
        }
      }
      &.has-sorting {
        cursor: pointer;
      }
      &.selected {
        background-color: variables.$color-primary;
        td {
          .list-group__button {
            :deep(img) {
              filter: brightness(100);
            }
          }
          p {
            color: white;
          }
        }
      }
      &:hover {
        cursor: pointer;
      }
      &:hover:not(.selected) {
        background-color: rgba(205, 222, 254, 0.2);
        td {
          .list-group__button {
            background-color: rgba(205, 222, 254, 0.2);
          }
        }
      }
      &:last-child {
        border-radius: 0;
      }
      &:not(:first-child) {
        border-top: none;
      }
    }
  }
}
</style>
